import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type UserCompanyNotificationDto, markNotificationAsRead } from '@/services/ws-comments/src/main/resources/rest-api';
import { fromDateTimeType } from '@/js/libs/api-helpers';
import { ellipsize } from '@/js/libs/ellipsize';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { type AppBarNotificationItem, AppBarNotifications } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import DateTime from 'src/components/DateTime/index.tsx';
import { SystemNotificationModal } from 'src/components/SystemNotificationModal.tsx';
import { useNavigationContext } from './Navigation/context.tsx';

const TITLE_MAX_LENGTH = 100;

type CriticalNotificationsProps = {
  onHeightChange: (height: number) => void;
};

export const CriticalNotifications = memo(function CriticalNotifications({ onHeightChange }: CriticalNotificationsProps) {
  const [t] = useTranslation(['common']);
  const { criticalNotifications, dismissCriticalNotification, reloadCriticalNotifications, reloadUnreadSystemNotificationsCount } =
    useNavigationContext();
  const previewModalState = useModalState<UserCompanyNotificationDto>();

  const markNotificationAsReadRequest = useLazyRequest({ apiMethod: markNotificationAsRead });

  const appBarItems = useMemo(
    () =>
      criticalNotifications.map<AppBarNotificationItem>(({ id, title, startDate }) => ({
        id,
        title: ellipsize(title, TITLE_MAX_LENGTH),
        meta: <DateTime value={fromDateTimeType(startDate)} showLocal />,
      })),
    [criticalNotifications],
  );

  const openNotification = useCallbackRef((id: string) => {
    const notification = criticalNotifications.find((item) => item.id === id);
    if (notification) previewModalState.open(notification);
  });

  const readNotification = useCallbackRef(async (id: string) => {
    await markNotificationAsReadRequest.load({ id });
    reloadUnreadSystemNotificationsCount();
    reloadCriticalNotifications();
  });

  return (
    <>
      {appBarItems.length > 0 && (
        <AppBarNotifications
          type='warning'
          items={appBarItems}
          actionLabel={t('common:SHOW_DETAILS')}
          onAction={openNotification}
          onClose={dismissCriticalNotification}
          onHeightChange={onHeightChange}
        />
      )}
      <SystemNotificationModal state={previewModalState} onRead={readNotification} />
    </>
  );
});
